// What the Mac asks of `NLTagger` (lexical classes and lemmas), as an injected interface, with a deterministic English
// fallback. APPROXIMATE, clearly: the fallback is a word list plus suffix rules, not a trained tagger.
//
// Where it matters:
//   - Repeated words (style linter): which words count (the Mac takes nouns, verbs, adjectives and adverbs — the
//     fallback takes every word that isn't in its function-word lists) and which forms are one word (the Mac groups by
//     lemma; the fallback by `stemKey`, below). Results differ when a word is a function word the lists miss, when two
//     forms of a word stem apart (irregular verbs outside the table: "rose"/"risen"), or when two different words stem
//     together ("hoped"/"hopped" → "hop"), and at contractions, which the Mac tokenises as two words ("did", "n't") and
//     this as one — that shifts the "within about 40 words" window by the contractions between two repeats.
//   - Overused words, "crowded scenes" (describing words: adjective, adverb or verb): the fallback's parts of speech
//     come from suffixes (-ly adverbs; -ed/-ing verbs; -ous/-ful/-less/-able/-ible/-ive/-ish/-ic/-al adjectives, doubled
//     consonant + y as in "shabby"; short lists of common adjectives, adverbs and verbs) and call anything else a noun.
//     A crowded noun the rules read as an adjective ("the county" → noun, fine; "the jelly" → adjective) may be flagged,
//     and a crowded adjective with no telling suffix ("the bleak sky") may be missed.
//   - Word frequency's "group forms" option is Apple-only: it shows each group under its dictionary form, which a stem
//     can't give ("mak"), so it runs only with a real tagger injected.
// The crutch-word part of Overused words, filter verbs and dialogue tags need no tagger and are exact.

export type LexicalClass =
  | 'noun' | 'verb' | 'adjective' | 'adverb' | 'pronoun' | 'determiner' | 'particle' | 'preposition' | 'number'
  | 'conjunction' | 'interjection' | 'otherWord';

/** The subset of `NLTagger` the Revise checks use. `range` is UTF-16 [from, to) of a word in `text`. */
export interface LanguageTagger {
  /** True when this is a real tagger (the Mac's results); false for the approximate fallback. */
  readonly exact: boolean;
  lexicalClass(text: string, range: [number, number]): LexicalClass | null;
  /** The word's lemma, lower case (the fallback's is a stem key: see `stemKey`). */
  lemma(text: string, range: [number, number]): string | null;
}

const words = (s: string) => new Set(s.split(/\s+/).filter(Boolean));

const PRONOUNS = words(`i me my mine myself you your yours yourself yourselves he him his himself she her hers herself it its itself we
  us our ours ourselves they them their theirs themselves who whom whose which what whoever whomever whatever whichever
  someone somebody something anyone anybody anything everyone everybody everything nobody nothing none one oneself
  another each other either neither`);
const DETERMINERS = words('a an the this that these those some any every no all both few many much several such');
const PREPOSITIONS = words(`about above across after against along amid amidst among amongst around as at before behind below beneath
  beside besides between beyond by despite down during except for from in inside into like near of off on onto opposite
  out outside over past per round since than through throughout till to toward towards under underneath unlike until
  up upon via with within without`);
const CONJUNCTIONS = words('and but or nor so yet because although though whereas while whilst unless if whether');
const PARTICLES = words('not to');
const INTERJECTIONS = words('oh ah hey yes no okay ok yeah hello goodbye wow alas');
const AUXILIARIES = words(`be am is are was were been being have has had having do does did done doing will would shall should can could
  may might must ought`);
const ADVERBS = words(`very quite rather too so just then now soon still even almost never always often sometimes seldom rarely
  again already also here there where when why how away back together however instead perhaps maybe indeed
  yesterday today tomorrow tonight once twice later ago else ever forth further hence meanwhile moreover nevertheless
  otherwise somehow somewhere anywhere everywhere nowhere thus therefore up down out inside outside anyway afterwards
  sideways upstairs downstairs abroad ahead apart aside around almost enough little less least more most well fast hard`);
const ADJECTIVES = words(`good bad big small large little old new young long short high low dark light cold hot warm cool bright dim
  quiet loud soft hard wet dry full empty clean dirty rich poor strong weak thick thin heavy wide narrow deep flat
  sharp dull fresh stale red orange yellow green blue purple pink brown black white grey gray pale bleak grim stark
  calm wild tame kind cruel brave afraid alone alive dead sure ready able sorry tired happy sad angry glad great
  whole real true false free late early near far clear plain strange odd rough smooth sweet bitter sour faint vast
  tiny huge proud shy slow quick fierce gentle harsh bare silent still pretty ugly lovely lonely silly holy friendly
  likely lively ghastly deadly`);
const VERBS = words(`say says said go goes went gone come comes came see sees saw seen know knows knew known think thinks thought
  take takes took taken make makes made get gets got give gives gave given find finds found tell tells told feel feels
  felt become became leave leaves left put puts bring brings brought begin begins began begun keep keeps kept hold
  holds held stand stands stood hear hears heard let lets mean means meant set sets meet meets met run runs ran
  pay pays paid sit sits sat speak speaks spoke spoken lie lies lay lain lead leads led grow grows grew grown lose
  loses lost fall falls fell fallen send sends sent build builds built understand understands understood draw draws
  drew drawn break breaks broke broken spend spends spent cut cuts rise rises rose risen drive drives drove driven
  buy buys bought wear wears wore worn choose chooses chose chosen seem seems look looks turn turns want wants`);

/** Past and plural forms the suffix rules can't reach, to their dictionary forms. */
const IRREGULAR: Record<string, string> = {
  went: 'go', gone: 'go', came: 'come', saw: 'see', seen: 'see', knew: 'know', known: 'know', thought: 'think', took: 'take',
  taken: 'take', made: 'make', got: 'get', gotten: 'get', gave: 'give', given: 'give', told: 'tell', felt: 'feel', became: 'become',
  left: 'leave', brought: 'bring', began: 'begin', begun: 'begin', kept: 'keep', held: 'hold', stood: 'stand', heard: 'hear',
  meant: 'mean', ran: 'run', paid: 'pay', sat: 'sit', spoke: 'speak', spoken: 'speak', grew: 'grow', grown: 'grow', lost: 'lose',
  fell: 'fall', fallen: 'fall', sent: 'send', built: 'build', understood: 'understand', drew: 'draw', drawn: 'draw',
  broke: 'break', broken: 'break', spent: 'spend', rose: 'rise', risen: 'rise', drove: 'drive', driven: 'drive', bought: 'buy',
  wore: 'wear', worn: 'wear', chose: 'choose', chosen: 'choose', wrote: 'write', written: 'write', ate: 'eat', eaten: 'eat',
  caught: 'catch', taught: 'teach', fought: 'fight', sought: 'seek', slept: 'sleep', swept: 'sweep', wept: 'weep', crept: 'creep',
  knelt: 'kneel', dealt: 'deal', dreamt: 'dream', learnt: 'learn', burnt: 'burn', spoilt: 'spoil', froze: 'freeze',
  frozen: 'freeze', stole: 'steal', stolen: 'steal', woke: 'wake', woken: 'wake', shook: 'shake', shaken: 'shake', flew: 'fly',
  flown: 'fly', threw: 'throw', thrown: 'throw', blew: 'blow', blown: 'blow', sang: 'sing', sung: 'sing', rang: 'ring',
  rung: 'ring', swam: 'swim', swum: 'swim', hid: 'hide', hidden: 'hide', bit: 'bite', bitten: 'bite', forgot: 'forget',
  forgotten: 'forget', forgave: 'forgive', forgiven: 'forgive', clung: 'cling', flung: 'fling', swung: 'swing', stung: 'sting',
  struck: 'strike', stuck: 'stick', shone: 'shine', shot: 'shoot', slid: 'slide', strode: 'stride', stridden: 'stride',
  children: 'child', men: 'man', women: 'woman', feet: 'foot', teeth: 'tooth', mice: 'mouse', geese: 'goose', people: 'person',
  was: 'be', were: 'be', is: 'be', are: 'be', am: 'be', been: 'be', has: 'have', had: 'have', does: 'do', did: 'do',
};

const VOWEL = /[aeiouy]/;
const DOUBLE = /([bdgkmnprt])\1$/;
const vowelGroups = (s: string) => (s.match(/[aeiouy]+/g) ?? []).length;

/** Adds back the silent e an -ed/-ing ending took, as far as spelling tells ("hoping" → "hope", "walking" → "walk"). */
function restoreE(base: string): string {
  if (DOUBLE.test(base)) return base.slice(0, -1); // running → run
  if (/[vcz]$/.test(base) || /u$/.test(base)) return base + 'e'; // leave, notice, argue
  if (vowelGroups(base) === 1 && /[^aeiou][aeiou][^aeiouwxy]$/.test(base)) return base + 'e'; // hope, stare, close
  return base;
}

/** A best-effort dictionary form (lower case): the irregular table, then suffix rules. */
export function approximateLemma(word: string): string {
  const w = word.toLowerCase().replaceAll('’', "'");
  if (IRREGULAR[w]) return IRREGULAR[w];
  if (w.length > 4 && w.endsWith('ies')) return w.slice(0, -3) + 'y';
  if (w.length > 4 && w.endsWith('ied')) return w.slice(0, -3) + 'y';
  if (w.length >= 6 && w.endsWith('ing') && VOWEL.test(w.slice(0, -3))) return restoreE(w.slice(0, -3));
  if (w.length >= 5 && w.endsWith('ed') && VOWEL.test(w.slice(0, -2)) && !w.endsWith('eed')) return restoreE(w.slice(0, -2));
  if (w.length > 4 && /(sses|shes|ches|xes|zes|oes)$/.test(w)) return w.slice(0, -2);
  if (w.length > 3 && w.endsWith('s') && !/(ss|us|is|'s)$/.test(w)) return w.slice(0, -1);
  if (w.endsWith("'s")) return w.slice(0, -2);
  return w;
}

/** The fallback's grouping key: the lemma without a final e, so forms whose e the rules misjudge still meet
 *  ("raise"/"raised"/"raising" → "rais"). Not a word to show. */
export function stemKey(word: string): string {
  const l = approximateLemma(word);
  return l.length > 3 && l.endsWith('e') ? l.slice(0, -1) : l;
}

/** A part of speech from word lists and suffixes (see the header for what it gets wrong). */
export function approximateLexicalClass(word: string): LexicalClass {
  const w = word.toLowerCase().replaceAll('’', "'");
  if (/^\p{N}+$/u.test(w)) return 'number';
  if (PRONOUNS.has(w)) return 'pronoun';
  if (DETERMINERS.has(w)) return 'determiner';
  if (PREPOSITIONS.has(w)) return 'preposition';
  if (CONJUNCTIONS.has(w)) return 'conjunction';
  if (PARTICLES.has(w)) return 'particle';
  if (INTERJECTIONS.has(w)) return 'interjection';
  if (AUXILIARIES.has(w) || VERBS.has(w) || IRREGULAR[w]) return 'verb';
  if (ADJECTIVES.has(w)) return 'adjective';
  if (ADVERBS.has(w)) return 'adverb';
  if (w.length > 4 && w.endsWith('ly')) return 'adverb';
  if (w.length > 5 && w.endsWith('ing')) return 'verb';
  if (w.length > 4 && w.endsWith('ed')) return 'verb';
  if (/(ous|ful|less|able|ible|ive|ish|ic|ical)$/.test(w) && w.length > 4) return 'adjective';
  if (w.length > 5 && w.endsWith('al')) return 'adjective';
  if (/([bdgmnprtz])\1y$/.test(w)) return 'adjective'; // shabby, soggy, muddy
  return 'noun';
}

/** The deterministic fallback for `LanguageTagger` (English only). */
export const approximateTagger: LanguageTagger = {
  exact: false,
  lexicalClass: (text, [a, b]) => approximateLexicalClass(text.slice(a, b)),
  lemma: (text, [a, b]) => stemKey(text.slice(a, b)),
};

/** Why Word frequency's grouping by dictionary form isn't offered without a real tagger (for the UI). */
export const GROUP_FORMS_APPLE_ONLY = 'Grouping forms under their dictionary form needs Apple’s language tagger, which the web doesn’t have.';
